/**
 * ULID generation.
 *
 * ULIDs rather than UUIDs because they sort by creation time. That matters here more than usual:
 * scores and check-ins are generated on a judge's or a gate worker's device, possibly offline for
 * an hour, and arrive out of order. A sortable id means the natural ordering of the rows is the
 * order they were actually entered in, without a separate sequence column that the offline device
 * could not have known.
 *
 * Client generation is also what makes submission idempotent: the same score retried after a
 * dropped connection carries the same id and upserts rather than duplicating.
 *
 * Randomness is injected so this stays pure and testable. The default reads the Web Crypto global,
 * which exists in Workers and browsers; React Native needs `react-native-get-random-values`
 * imported once at app startup, which is why the seam is here rather than assumed away.
 */

import { MAX_EVENT_NUMBER, MAX_STATION, STATION_BLOCK_SIZE } from "@powwow/validation";

const ENCODING = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"; // Crockford base32: no I, L, O or U.
const ENCODING_LENGTH = 32;
const TIME_LENGTH = 10;
const RANDOM_LENGTH = 16;
const MAX_TIME = 281_474_976_710_655; // 2^48 - 1

export type RandomBytes = (length: number) => Uint8Array;

export const webCryptoRandom: RandomBytes = (length) => {
  const bytes = new Uint8Array(length);
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (!cryptoRef?.getRandomValues) {
    throw new Error(
      "No secure random source. In React Native, import 'react-native-get-random-values' once at startup.",
    );
  }
  cryptoRef.getRandomValues(bytes);
  return bytes;
};

function encodeTime(now: number): string {
  if (!Number.isInteger(now) || now < 0 || now > MAX_TIME) {
    throw new RangeError(`Timestamp out of ULID range: ${now}`);
  }
  let remaining = now;
  let out = "";
  for (let i = TIME_LENGTH - 1; i >= 0; i -= 1) {
    const mod = remaining % ENCODING_LENGTH;
    out = ENCODING[mod] + out;
    remaining = (remaining - mod) / ENCODING_LENGTH;
  }
  return out;
}

function encodeRandom(random: RandomBytes): string {
  const bytes = random(RANDOM_LENGTH);
  let out = "";
  for (let i = 0; i < RANDOM_LENGTH; i += 1) {
    out += ENCODING[(bytes[i] as number) % ENCODING_LENGTH];
  }
  return out;
}

/**
 * A ULID for `at`, which defaults to now.
 *
 * `at` is a parameter because a batch written for one logical moment should share a prefix, and
 * because a test that cannot control the timestamp cannot assert on ordering.
 */
export function ulid(at: number = Date.now(), random: RandomBytes = webCryptoRandom): string {
  return encodeTime(Math.trunc(at)) + encodeRandom(random);
}

const ULID_PATTERN = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;

export function isUlid(value: string): boolean {
  return ULID_PATTERN.test(value);
}

/** The instant encoded in a ULID. Useful for ordering rows that arrived out of order. */
export function ulidTime(value: string): number {
  let time = 0;
  for (const char of value.slice(0, TIME_LENGTH)) {
    const index = ENCODING.indexOf(char);
    if (index === -1) throw new RangeError(`Not a ULID: ${value}`);
    time = time * ENCODING_LENGTH + index;
  }
  return time;
}

/**
 * A short numeric access code for a judge (D15).
 *
 * Read aloud across a drum, typed on a phone in sunlight, and bound to the first device that
 * presents it. It is short because it has to be, and that is safe because it is scoped to one
 * session, expires on its own, is revocable in one tap, and — critically — its verification is
 * rate-limited and single-binding, so a short code is not a guessable one in practice.
 *
 * Digits only, and never starting with a zero, because a leading zero is the thing people drop
 * when writing a code down.
 */
export function accessCode(length = 6, random: RandomBytes = webCryptoRandom): string {
  if (length < 4 || length > 10) throw new RangeError("Access codes are 4–10 digits");
  const bytes = random(length);
  let code = String(1 + ((bytes[0] as number) % 9));
  for (let i = 1; i < length; i += 1) {
    code += String((bytes[i] as number) % 10);
  }
  return code;
}

/**
 * A high-entropy token for a magic link, a session cookie or a participant's `/manage/{token}`
 * link. Unguessable is the requirement (§37), so this is 32 bytes of randomness rendered in
 * base32 — URL-safe, case-insensitive, and printable on a QR slip without escaping.
 */
export function secureToken(bytes = 32, random: RandomBytes = webCryptoRandom): string {
  const raw = random(bytes);
  let out = "";
  for (const byte of raw) {
    out += ENCODING[byte % ENCODING_LENGTH];
  }
  return out;
}

/**
 * The registration number: the human-readable id printed on the slip, said at the desk, and typed
 * into a search box.
 *
 * Distinct from the dancer number — which is worn in the arena and scored by, is optional, and can
 * be reassigned — this identifies the *registration itself*. Every registration has one, it never
 * changes, and it is what an export, a payout sheet or a check-in card carries to say *which
 * registration* a row is. It is sequential per event and zero-padded so it reads and sorts the way
 * a numbered list should, and prefixed so it is recognisable beside other ids in a column.
 *
 * Not random: sequential is easier to read aloud and communicates how many have registered, which
 * is the desk's own running count. The prefix is fixed here rather than configured — a community
 * that wants its own numbering is a later decision, not a reason to make the pilot's number a
 * setting nobody asked for.
 */
export const REGISTRATION_NUMBER_PREFIX = "REG-";

export function formatRegistrationNumber(sequence: number): string {
  return `${REGISTRATION_NUMBER_PREFIX}${String(sequence).padStart(4, "0")}`;
}

/** The numeric part of a registration number, or null if the string is not one we issued. */
export function registrationNumberSequence(code: string | null): number | null {
  if (!code || !code.startsWith(REGISTRATION_NUMBER_PREFIX)) return null;
  const digits = code.slice(REGISTRATION_NUMBER_PREFIX.length);
  if (!/^\d+$/.test(digits)) return null;
  return Number(digits);
}

/**
 * A desk, identified by a number the desk itself is told.
 *
 * Both numbers a registration carries are allocated by reading the highest one issued and adding
 * one, which is correct with a single writer and silently wrong with two: two desks that cannot see
 * each other — offline at a gate, or simply submitting in the same second — both read 41 and both
 * issue 42. The unique indexes catch that on the way into the database, but by then a person has
 * been told their number and walked away with it (D86).
 *
 * So a desk that has been given a station number draws from its own band of the sequence, and two
 * stations can never produce the same number without ever having to agree on anything. The band is
 * *derived* from the station number rather than allocated, which is the property that makes it work
 * with no signal: a desk set up on the Saturday morning in a field needs to be told "you are desk
 * 2" and nothing else.
 *
 * **An event that never sets a station is unchanged** — numbers stay 1, 2, 3 and REG-0001, REG-0002,
 * and neither the bands nor the ceiling apply to it. That matters more than the feature does: nearly
 * every community runs one desk, and they must not pay for a second one they do not have.
 *
 * **The awkward case is an event with both**, and it is not hypothetical: the public registration
 * form has no station and never will, because it is a stranger's phone. An unstationed writer taking
 * the highest number in the event would read desk 3's 2001 and issue 2002 — the collision this was
 * built to prevent, arriving through the front door. So when an event has stationed desks in it, the
 * unstationed sequence steps around their bands: `stationsInUse` is what says so, and the repository
 * knows it from the `station` column it selects alongside the numbers.
 *
 * `STATION_BLOCK_SIZE` and `MAX_STATION` live in `@powwow/validation` so that the schema which
 * refuses a bad station and the allocator which trusts a good one cannot drift apart.
 */

/** Options both allocators take. */
export interface NumberingOptions {
  /** The desk asking. Absent means the public form, or a desk that was never given a number. */
  station?: number | undefined;
  /**
   * Whether any desk at this event has a station. Only consulted when `station` is absent, and only
   * to keep the unstationed sequence out of the bands the stationed desks are using.
   */
  stationsInUse?: boolean | undefined;
}

/**
 * The band of the sequence a station may issue from, inclusive.
 *
 * Station 1 is 1–9,999 and station 2 is 10,001–19,999, so a number says which desk issued it —
 * useful when two desks' work is reconciled after the fact, and impossible to get from a number
 * allocated centrally.
 *
 * The gap at each block boundary is deliberate: `n * STATION_BLOCK_SIZE` belongs to no station, so a
 * fencepost error at either end of a band produces a number nobody issues rather than one the next
 * desk is about to.
 */
export function stationBand(station: number): { min: number; max: number } {
  if (!Number.isInteger(station) || station < 1 || station > MAX_STATION) {
    throw new Error(`Station must be a whole number from 1 to ${MAX_STATION}.`);
  }
  const base = (station - 1) * STATION_BLOCK_SIZE;
  return { min: base + 1, max: base + STATION_BLOCK_SIZE - 1 };
}

/**
 * Everything stations 2 and above may issue, as one span.
 *
 * Station 1 is left out on purpose: its band starts at 1, which is where an unstationed sequence
 * starts anyway, so the two are the same numbers arrived at two ways rather than a conflict. What an
 * unstationed sequence must not walk into is the *other* desks' bands, and this is them.
 */
const RESERVED = { min: stationBand(2).min, max: stationBand(MAX_STATION).max };

/**
 * The highest sequence already issued that this caller is allowed to issue, and the next one.
 *
 * Shared by both numbers because they are the same act on different formats. With no station and no
 * stations anywhere in the event, this is the plain maximum-plus-one it has always been.
 */
function nextSequence(
  sequences: Iterable<number | null>,
  { station, stationsInUse }: NumberingOptions,
): number {
  const band = station === undefined ? null : stationBand(station);
  /** Only when somebody else is using the bands. An event with one desk has the whole line. */
  const avoiding = band === null && stationsInUse === true;

  let highest = band ? band.min - 1 : 0;
  for (const sequence of sequences) {
    if (sequence === null) continue;
    if (band && (sequence < band.min || sequence > band.max)) continue;
    // Somebody else's band. Counting it is how the public form ends up one above a desk's latest.
    if (avoiding && sequence >= RESERVED.min && sequence <= RESERVED.max) continue;
    if (sequence > highest) highest = sequence;
  }

  let next = highest + 1;

  /**
   * Past the desks rather than into them.
   *
   * Only reachable when an unstationed sequence has run the whole way up to the first desk's band,
   * which needs a thousand registrations through the public form at an event that also runs numbered
   * desks. It resumes above every band, so the numbers stay unique and keep climbing.
   */
  if (avoiding && next >= RESERVED.min && next <= RESERVED.max) next = RESERVED.max + 1;

  if (band && next > band.max) {
    // Named rather than swallowed, because the alternative is issuing into the next desk's band and
    // finding out when two people are wearing the same number in an arena.
    throw new Error(
      `Desk ${station} has issued all ${(STATION_BLOCK_SIZE - 1).toLocaleString("en-CA")} of its ` +
        `numbers. Set this desk to an unused station number to carry on.`,
    );
  }

  if (next > MAX_EVENT_NUMBER) {
    // Unreachable by any pow wow that has ever happened, and named anyway: a number nobody can read
    // aloud is the failure this bound exists to prevent, so running past it has to say so.
    throw new Error(
      `This event has issued all ${MAX_EVENT_NUMBER.toLocaleString("en-CA")} of its numbers.`,
    );
  }
  return next;
}

/**
 * The next registration number for an event, given every number already issued in it.
 *
 * Reads the maximum rather than counting rows, so a cancelled registration does not cause the next
 * one to reuse its number — the same reason the dancer number and team number are computed this
 * way. Pure, so the sequence logic is tested without a database; each repository supplies the
 * event's existing numbers from its own scoped query.
 *
 * `options.station` narrows it to that desk's band. With neither a station nor any in the event, it
 * reads the whole event exactly as it always has.
 */
export function nextRegistrationNumber(
  existing: Iterable<string | null>,
  options: NumberingOptions = {},
): string {
  const sequences: number[] = [];
  for (const code of existing) {
    const sequence = registrationNumberSequence(code);
    if (sequence !== null) sequences.push(sequence);
  }
  return formatRegistrationNumber(nextSequence(sequences, options));
}

/**
 * The next dancer number — the same allocation, on a bare integer.
 *
 * Bare because it is worn on a back and called across an arena, and because two places sort by
 * `Number(dancerNumber)` to build the running order a judge scores down. A number that is not a
 * number sorts as `NaN`, which does not throw and does not sort — it silently shuffles the order
 * the arena director is calling from. That is why a station shows up as a *band* of the sequence
 * and never as a prefix or a letter.
 */
export function nextDancerNumber(
  existing: Iterable<string | null>,
  options: NumberingOptions = {},
): string {
  const sequences: number[] = [];
  for (const code of existing) {
    const parsed = Number(code);
    // A number typed by hand at the desk may be anything; only the ones we could have issued count.
    if (code !== null && code.trim() !== "" && Number.isInteger(parsed) && parsed > 0) {
      sequences.push(parsed);
    }
  }
  return String(nextSequence(sequences, options));
}
